/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Box, BoxProps } from '@chakra-ui/react';
import { F } from '@mobily/ts-belt';
import {
  Children,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { IconButton, IconButtonProps } from '../icon-button/icon-button';

type SliderContextValue = {
  sliderRef: React.RefObject<HTMLDivElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  itemsRef: React.MutableRefObject<Array<HTMLElement | null>>;
  sliderWidth: number;
  offset: number;
  slide: (direction: number) => void;
};

const SliderContext = createContext<SliderContextValue | null>(null);

type SliderProps = {
  children: React.ReactNode;
  onSlideChange?: (index: number) => void;
};

export function Slider({ children, onSlideChange }: SliderProps) {
  const sliderRef: SliderContextValue['sliderRef'] = useRef(null);
  const containerRef: SliderContextValue['containerRef'] = useRef(null);
  const itemsRef: SliderContextValue['itemsRef'] = useRef([]);

  const [sliderWidth, setSliderWidth] =
    useState<SliderContextValue['offset']>(0);

  const [currIdx, setCurrIdx] = useState(0);
  const [offsetMap, setOffsetMap] = useState<number[]>([0]);
  const [offset, setOffset] = useState(offsetMap[currIdx]);
  useLayoutEffect(() => {
    const initializeSlider = () => {
      if (sliderRef.current && containerRef.current) {
        const actualContainerWidth = Math.ceil(
          containerRef.current.getBoundingClientRect().width
        );
        const { scrollWidth } = sliderRef.current;
        const sliderWidth = scrollWidth - actualContainerWidth;

        setSliderWidth(sliderWidth);

        const offsetMap = [0];
        let currOffset = 0;
        let currentItemIdx = 0;
        while (
          currOffset < sliderWidth &&
          currentItemIdx < itemsRef.current.length
        ) {
          const currentItem = itemsRef.current[currentItemIdx];
          currentItemIdx++;

          if (!currentItem) continue;

          const itemOffset = currentItem.offsetLeft;
          if (itemOffset - currOffset > actualContainerWidth * 0.5) {
            offsetMap.push(itemOffset);
            currOffset = itemOffset;
          }
        }

        if (offsetMap[offsetMap.length - 1] < sliderWidth) {
          offsetMap.push(sliderWidth);
        }
        setOffsetMap(offsetMap);
      }
    };

    const throttledInitializer = F.throttle(initializeSlider, 1000);

    initializeSlider();

    window?.addEventListener('resize', throttledInitializer);
    return () => {
      window?.removeEventListener('resize', throttledInitializer);
    };
  }, []);

  const move = useCallback(
    (index: number) => {
      setCurrIdx(index);
      setOffset(offsetMap[index]);
      typeof onSlideChange === 'function' && onSlideChange(index);
    },
    [offsetMap, onSlideChange]
  );

  const slide = useCallback(
    (direction: number) => {
      const nextIndex = clamp(0, offsetMap.length - 1, currIdx + direction);
      move(nextIndex);
    },
    [currIdx, move, offsetMap.length]
  );

  const sliderContext: SliderContextValue = useMemo(
    () => ({
      sliderRef,
      containerRef,
      itemsRef,
      sliderWidth,
      offset,
      slide,
    }),
    [offset, slide, sliderWidth]
  );

  return (
    <SliderContext.Provider value={sliderContext}>
      {children}
    </SliderContext.Provider>
  );
}

function useSlider() {
  const context = useContext(SliderContext);
  if (!context) {
    throw new Error('`useSlider` must be used inside of `SliderContext`');
  }
  return context;
}

type SliderContainerProps = BoxProps;

export function SliderContainer(props: SliderContainerProps) {
  const { containerRef, sliderRef, sliderWidth, offset, itemsRef } =
    useSlider();

  const slides = useMemo(() => {
    const children = Children.toArray(props.children);
    return children.map((child, i) => {
      return (
        <Box ref={(el) => (itemsRef.current[i] = el)} key={`slide-item-${i}`}>
          {child}
        </Box>
      );
    });
  }, [itemsRef, props.children]);

  return (
    <Box ref={containerRef} overflowX="hidden" {...props}>
      <Box
        ref={sliderRef}
        display="flex"
        w="full"
        transition="transform 0.5s"
        transform={`translateX(${-clamp(0, sliderWidth, offset)}px)`}
        willChange="transform"
      >
        {slides}
      </Box>
    </Box>
  );
}

type SliderTriggerProps = IconButtonProps & {
  direction?: 'left' | 'right';
  asChild?: boolean;
};

export function SliderTrigger({
  children,
  direction = 'left',
  ...props
}: SliderTriggerProps) {
  const { slide, offset, sliderWidth } = useSlider();

  const isStart = offset === 0;
  const isEnd = offset >= sliderWidth;

  return (
    <IconButton
      isDisabled={
        (direction === 'right' && isEnd) || (direction === 'left' && isStart)
      }
      onClick={() => slide(direction === 'right' ? 1 : -1)}
      {...props}
    >
      {children}
    </IconButton>
  );
}

type SliderItemProps = BoxProps;

export const SliderItem = forwardRef<HTMLDivElement, SliderItemProps>(function (
  { children, className, ...props },
  ref
) {
  return (
    <Box ref={ref} w="auto" flex="0" {...props}>
      {children}
    </Box>
  );
});

SliderItem.displayName = 'SliderItem';

// If you need a search function in the future, uncomment this:
/*
const searchOffsetRight =
  (offset: number, sliderContainerWidth: number) =>
  (item: HTMLElement | null) =>
    item && item.offsetLeft - offset > sliderContainerWidth;
*/

function clamp(min: number, max: number, v: number) {
  return Math.max(min, Math.min(v, max));
}
